// ─────────────────────────────────────────────────────────────────
// Cloud storage helper — wraps Firebase Storage with quota tracking
// ─────────────────────────────────────────────────────────────────
// Path layout:
//   users/{uid}/designs/{designId}/build.zip          ← the generated artifact
//   users/{uid}/designs/{designId}/manifest.json      ← metadata for dashboard
//
// Quota: enforced client-side in this file. The hard cap (and what
// stops abuse from a forged client) lives in storage.rules — see
// storage.rules in the project root for the server-side rule.
// ─────────────────────────────────────────────────────────────────

import {
  ref, uploadBytes, getDownloadURL, listAll, getMetadata, deleteObject,
  uploadString,
} from 'firebase/storage';
import { firebaseStorage, firebaseAuth } from './firebase';

// Plan-aware quota. Pulled from PLAN_FEATURES at the call site so this
// module stays decoupled from app state.
const MB = 1024 * 1024;

function userBasePath(uid) {
  return `users/${uid}/designs`;
}

function designPath(uid, designId) {
  return `${userBasePath(uid)}/${designId}`;
}

/**
 * Sum the bytes used by `users/{uid}/...`.
 * Walks the whole tree (Storage's listAll returns immediate children +
 * subfolders, so we recurse).
 */
export async function getCloudUsage(uid) {
  if (!uid) return { bytes: 0, items: [] };
  const root = ref(firebaseStorage, userBasePath(uid));
  let totalBytes = 0;
  const items = [];

  async function walk(folderRef) {
    let res;
    try {
      res = await listAll(folderRef);
    } catch (err) {
      // First-time users have no folder — that's not an error.
      if (err?.code === 'storage/object-not-found') return;
      throw err;
    }
    for (const item of res.items) {
      try {
        const meta = await getMetadata(item);
        totalBytes += meta.size || 0;
        items.push({
          path: item.fullPath,
          name: item.name,
          size: meta.size || 0,
          updated: meta.updated,
          contentType: meta.contentType,
        });
      } catch { /* ignore individual item errors */ }
    }
    for (const sub of res.prefixes) await walk(sub);
  }

  await walk(root);
  return { bytes: totalBytes, items };
}

/**
 * Upload a generated zip + its manifest. Enforces quota client-side.
 * Throws { code, message } on quota or auth failure.
 */
export async function uploadDesignBuild({ uid, designId, designName, zipBlob, manifest, quotaMB }) {
  if (!uid)      throw { code: 'no-uid',      message: 'Not signed in.' };
  if (!designId) throw { code: 'no-design-id', message: 'Design id is required.' };
  if (!zipBlob)  throw { code: 'no-blob',     message: 'Zip blob is empty.' };

  // Quota check — sum existing usage minus this design's prior version
  // (so re-saving doesn't double-count).
  const { bytes: usedBytes, items } = await getCloudUsage(uid);
  const thisDesignBytes = items
    .filter(i => i.path.startsWith(`${designPath(uid, designId)}/`))
    .reduce((a, b) => a + (b.size || 0), 0);
  const projected = usedBytes - thisDesignBytes + zipBlob.size;
  const quotaBytes = (quotaMB || 0) * MB;
  if (projected > quotaBytes) {
    throw {
      code: 'quota-exceeded',
      message: `Storing this build would exceed your ${quotaMB} MB quota. Used ${(usedBytes/MB).toFixed(1)} MB · this build ${(zipBlob.size/MB).toFixed(1)} MB.`,
      usedBytes, projected, quotaBytes,
    };
  }

  // Upload the zip
  const zipRef = ref(firebaseStorage, `${designPath(uid, designId)}/build.zip`);
  const zipResult = await uploadBytes(zipRef, zipBlob, {
    contentType: 'application/zip',
    customMetadata: { designId, designName: designName || '' },
  });
  const downloadURL = await getDownloadURL(zipResult.ref);

  // Upload the manifest as JSON (small)
  const manifestPayload = {
    designId,
    designName: designName || 'untitled',
    generatedAt: new Date().toISOString(),
    zipBytes: zipBlob.size,
    downloadURL,
    ...manifest,
  };
  const manifestRef = ref(firebaseStorage, `${designPath(uid, designId)}/manifest.json`);
  await uploadString(manifestRef, JSON.stringify(manifestPayload, null, 2), 'raw', {
    contentType: 'application/json',
  });

  return { downloadURL, manifest: manifestPayload, zipBytes: zipBlob.size };
}

/**
 * List all stored designs for a user. Returns an array of manifest
 * objects (one per design) sorted newest-first.
 */
export async function listStoredDesigns(uid) {
  if (!uid) return [];
  const root = ref(firebaseStorage, userBasePath(uid));
  let res;
  try {
    res = await listAll(root);
  } catch (err) {
    if (err?.code === 'storage/object-not-found') return [];
    throw err;
  }

  // Each prefix is a designId folder
  const results = [];
  for (const folder of res.prefixes) {
    try {
      const manifestRef = ref(firebaseStorage, `${folder.fullPath}/manifest.json`);
      const url = await getDownloadURL(manifestRef);
      const r = await fetch(url);
      if (!r.ok) continue;
      const manifest = await r.json();
      // Refresh the build's download URL too — they expire on Firebase
      const zipRef = ref(firebaseStorage, `${folder.fullPath}/build.zip`);
      try {
        manifest.downloadURL = await getDownloadURL(zipRef);
      } catch {
        manifest.downloadURL = null; // zip missing
      }
      results.push(manifest);
    } catch {
      // Skip folders without a readable manifest
    }
  }
  results.sort((a, b) => (b.generatedAt || '').localeCompare(a.generatedAt || ''));
  return results;
}

/**
 * Delete a stored design (zip + manifest).
 */
export async function deleteStoredDesign(uid, designId) {
  if (!uid || !designId) return false;
  const folder = designPath(uid, designId);
  const zipRef = ref(firebaseStorage, `${folder}/build.zip`);
  const manifestRef = ref(firebaseStorage, `${folder}/manifest.json`);
  try { await deleteObject(zipRef);      } catch {}
  try { await deleteObject(manifestRef); } catch {}
  return true;
}

/**
 * Get a fresh signed download URL for a previously-uploaded build.
 */
export async function getDesignDownloadURL(uid, designId) {
  if (!uid || !designId) return null;
  const zipRef = ref(firebaseStorage, `${designPath(uid, designId)}/build.zip`);
  try {
    return await getDownloadURL(zipRef);
  } catch (err) {
    if (err?.code === 'storage/object-not-found') return null;
    throw err;
  }
}
